import React, { useState, useEffect } from 'react';
import { X, Pizza, Beef, Sandwich, Drumstick, UtensilsCrossed, Wheat, Salad } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { menuData } from '../data/mockData';

const categoryIcons = {
  pizzas: Pizza,
  burgers: Beef,
  sandwiches: Sandwich,
  panuozzo: Wheat,
  salades: Salad,
  texmex: Drumstick,
  autres: UtensilsCrossed
};

const categoryLabels = {
  pizzas: 'Pizzas',
  burgers: 'Burgers',
  sandwiches: 'Sandwichs',
  panuozzo: 'Panuozzo',
  salades: 'Nos Salades',
  texmex: 'Tex-Mex',
  autres: 'Autres'
};

export const MenuModal = ({ isOpen, onClose, initialCategory = 'burgers' }) => {
  const [activeCategory, setActiveCategory] = useState(initialCategory);

  useEffect(() => {
    if (isOpen) {
      setActiveCategory(initialCategory);
    }
  }, [isOpen, initialCategory]);

  const renderContent = () => {
    switch (activeCategory) {
      case 'pizzas':
        return renderPizzas();
      case 'burgers':
        return renderBurgers();
      case 'sandwiches':
        return renderSandwiches();
      case 'panuozzo':
        return renderPanuozzo();
      case 'salades':
        return renderSalades();
      case 'texmex':
        return renderTexMex();
      case 'autres':
        return renderAutres();
      default:
        return null;
    }
  };

  const renderPizzas = () => (
    <div className="space-y-6 md:space-y-8">
      {/* Offers section */}
      {menuData.pizzas.offers && (
        <Card className="bg-gradient-to-r from-red-600 to-red-500 backdrop-blur-sm border-none shadow-lg">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="text-xl md:text-2xl font-bold text-white">{menuData.pizzas.offers.title}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0">
            <ul className="space-y-2">
              {menuData.pizzas.offers.items.map((offer, idx) => (
                <li key={idx} className="text-white font-semibold text-sm md:text-base flex items-center">
                  <span className="mr-2">🎁</span>
                  <span>{offer}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      
      {menuData.pizzas.categories.map((category, idx) => (
        <Card key={idx} className="bg-[#D4A85F] backdrop-blur-sm border-none shadow-lg">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="text-xl md:text-2xl font-bold text-black">{category.subtitle}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {category.items.map((item, itemIdx) => (
                <div key={itemIdx} className="flex justify-between items-start p-3 md:p-4 bg-white/50 rounded-lg hover:bg-white/70 transition-all duration-200">
                  <div className="flex-1 min-w-0 pr-2">
                    <div className="flex flex-wrap items-center gap-1 md:gap-2">
                      <h4 className="font-bold text-base md:text-lg text-black">{item.name}</h4>
                      {item.featured && (
                        <Badge className="bg-black text-white text-xs">Signature</Badge>
                      )}
                      {item.special && (
                        <Badge className="bg-red-500 text-white text-xs">Promo</Badge>
                      )}
                    </div>
                    <p className="text-xs md:text-sm text-gray-700 mt-1 line-clamp-2">{item.description}</p>
                  </div>
                  <span className="font-bold text-black whitespace-nowrap text-sm md:text-base">{item.price}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderBurgers = () => (
    <div className="space-y-6 md:space-y-8">
      <Card className="bg-[#D4A85F] backdrop-blur-sm border-none shadow-lg">
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-xl md:text-2xl font-bold text-black">{menuData.burgers.classic.subtitle}</CardTitle>
          <div className="mt-3 p-3 bg-black/10 rounded-lg">
            <p className="text-xs md:text-sm font-bold text-black">{menuData.burgers.classic.note}</p>
          </div>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {menuData.burgers.classic.items.map((item, idx) => (
              <div key={idx} className="p-3 md:p-4 bg-white/50 rounded-lg hover:bg-white/70 transition-all duration-200">
                <h4 className="font-bold text-base md:text-lg text-black mb-1">{item.name}</h4>
                {item.description && (
                  <p className="text-xs text-gray-600 mb-2">{item.description}</p>
                )}
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs md:text-sm text-gray-700">Solo :</span>
                    <span className="font-bold text-black text-sm md:text-base">{item.soloPrice}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-black rounded-md">
                    <Badge className="bg-[#D4A85F] text-black font-bold text-xs hover:bg-[#E2B978]">
                      MENU
                    </Badge>
                    <span className="font-bold text-white text-base md:text-lg">{item.menuPrice}</span>
                  </div>
                  <p className="text-xs text-gray-600 italic">(frites + boisson 33cl)</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#D4A85F] backdrop-blur-sm border-none shadow-lg">
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-xl md:text-2xl font-bold text-black">{menuData.burgers.maison.subtitle}</CardTitle>
          {menuData.burgers.maison.note && (
            <div className="mt-3 p-3 bg-black/10 rounded-lg">
              <p className="text-xs md:text-sm font-bold text-black">{menuData.burgers.maison.note}</p>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {menuData.burgers.maison.items.map((item, idx) => (
              <div key={idx} className="flex justify-between items-start p-3 md:p-4 bg-white/50 rounded-lg hover:bg-white/70 transition-all duration-200">
                <div className="flex-1 min-w-0 pr-2">
                  <div className="flex flex-wrap items-center gap-1 md:gap-2">
                    <h4 className="font-bold text-base md:text-lg text-black">{item.name}</h4>
                    {item.featured && (
                      <Badge className="bg-black text-white text-xs">Signature</Badge>
                    )}
                  </div>
                  <p className="text-xs md:text-sm text-gray-700 mt-1 line-clamp-2">{item.description}</p>
                </div>
                <span className="font-bold text-black whitespace-nowrap text-sm md:text-base">{item.price}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderSandwiches = () => (
    <div className="space-y-6 md:space-y-8">
      <Card className="bg-[#D4A85F] backdrop-blur-sm border-none shadow-lg">
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-xl md:text-2xl font-bold text-black">{menuData.sandwiches.title}</CardTitle>
          <p className="text-xs md:text-sm text-gray-700 mt-2">{menuData.sandwiches.subtitle}</p>
          <p className="text-xs md:text-sm text-gray-600 mt-1">
            <span className="font-bold text-black">Boisson 33 cl : +1,50€</span>
            <span className="italic"> | Choix du pain : baguette ou pain maison</span>
          </p>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {menuData.sandwiches.items.map((item, idx) => (
              <div key={idx} className="flex justify-between items-start p-3 md:p-4 bg-white/50 rounded-lg hover:bg-white/70 transition-all duration-200">
                <div className="flex-1 min-w-0 pr-2">
                  <div className="flex flex-wrap items-center gap-1 md:gap-2">
                    <h4 className="font-bold text-base md:text-lg text-black">{item.name}</h4>
                    {item.featured && (
                      <Badge className="bg-black text-white text-xs">Signature</Badge>
                    )}
                  </div>
                  <p className="text-xs md:text-sm text-gray-700 mt-1 line-clamp-2">{item.description}</p>
                </div>
                <span className="font-bold text-black whitespace-nowrap text-sm md:text-base">{item.price}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Supplements section */}
      {menuData.sandwiches.supplements && (
        <Card className="bg-[#D4A85F] backdrop-blur-sm border-none shadow-lg">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="text-xl md:text-2xl font-bold text-black">{menuData.sandwiches.supplements.title}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0">
            <div className="space-y-3">
              {menuData.sandwiches.supplements.items.map((item, idx) => (
                <div key={idx} className="p-3 md:p-4 bg-white/50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <span className="font-bold text-black whitespace-nowrap text-sm md:text-base bg-black/10 px-2 py-1 rounded">{item.name}</span>
                    <p className="text-xs md:text-sm text-gray-700">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderPanuozzo = () => (
    <div className="space-y-6 md:space-y-8">
      <Card className="bg-[#D4A85F] backdrop-blur-sm border-none shadow-lg">
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-xl md:text-2xl font-bold text-black">{menuData.panuozzo.title}</CardTitle>
          {menuData.panuozzo.note && (
            <div className="mt-3 p-3 bg-black/10 rounded-lg">
              <p className="text-xs md:text-sm font-bold text-black">{menuData.panuozzo.note}</p>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {menuData.panuozzo.items.map((item, idx) => (
              <div key={idx} className="flex justify-between items-start p-3 md:p-4 bg-white/50 rounded-lg hover:bg-white/70 transition-all duration-200">
                <div className="flex-1 min-w-0 pr-2">
                  <div className="flex flex-wrap items-center gap-1 md:gap-2">
                    <h4 className="font-bold text-base md:text-lg text-black">{item.name}</h4>
                    {item.featured && (
                      <Badge className="bg-black text-white text-xs">Signature</Badge>
                    )}
                  </div>
                  <p className="text-xs md:text-sm text-gray-700 mt-1 line-clamp-2">{item.description}</p>
                </div>
                <span className="font-bold text-black whitespace-nowrap text-sm md:text-base">{item.price}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderSalades = () => (
    <div className="space-y-6 md:space-y-8">
      <Card className="bg-[#D4A85F] backdrop-blur-sm border-none shadow-lg">
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-xl md:text-2xl font-bold text-black">{menuData.salades.title}</CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {menuData.salades.items.map((item, idx) => (
              <div key={idx} className="flex justify-between items-start p-3 md:p-4 bg-white/50 rounded-lg hover:bg-white/70 transition-all duration-200">
                <div className="flex-1 min-w-0 pr-2">
                  <div className="flex flex-wrap items-center gap-1 md:gap-2">
                    <h4 className="font-bold text-base md:text-lg text-black">{item.name}</h4>
                    {item.featured && (
                      <Badge className="bg-black text-white text-xs">Signature</Badge>
                    )}
                  </div>
                  <p className="text-xs md:text-sm text-gray-700 mt-1 line-clamp-2">{item.description}</p>
                </div>
                <span className="font-bold text-black whitespace-nowrap text-sm md:text-base">{item.price}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderTexMex = () => (
    <div className="space-y-6 md:space-y-8">
      <Card className="bg-[#D4A85F] backdrop-blur-sm border-none shadow-lg">
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-xl md:text-2xl font-bold text-black">{menuData.texMex.menu.name}</CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="p-4 md:p-6 bg-white/50 rounded-lg">
            <p className="text-gray-700 mb-4 font-semibold text-sm md:text-base">{menuData.texMex.menu.description}</p>
            <div className="mb-4">
              <p className="font-semibold text-black mb-2 text-sm md:text-base">Choix du menu :</p>
              <ul className="space-y-1">
                {menuData.texMex.menu.choices.map((choice, idx) => (
                  <li key={idx} className="text-gray-700 flex items-start text-xs md:text-sm">
                    <span className="mr-2">•</span>
                    <span>{choice}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="font-bold text-xl md:text-2xl text-black">{menuData.texMex.menu.price}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#D4A85F] backdrop-blur-sm border-none shadow-lg">
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-xl md:text-2xl font-bold text-black">{menuData.texMex.manchons.title}</CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            {menuData.texMex.manchons.items.map((item, idx) => (
              <div key={idx} className="p-3 md:p-4 bg-white/50 rounded-lg hover:bg-white/70 transition-all duration-200 text-center">
                <p className="font-bold text-black text-sm md:text-base">{item.quantity}</p>
                <p className="text-base md:text-lg font-bold text-black mt-1">{item.price}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#D4A85F] backdrop-blur-sm border-none shadow-lg">
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-xl md:text-2xl font-bold text-black">{menuData.texMex.tenders.title}</CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            {menuData.texMex.tenders.items.map((item, idx) => (
              <div key={idx} className="p-3 md:p-4 bg-white/50 rounded-lg hover:bg-white/70 transition-all duration-200 text-center">
                <p className="font-bold text-black text-sm md:text-base">{item.quantity}</p>
                <p className="text-base md:text-lg font-bold text-black mt-1">{item.price}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tenders Tempura ou Donuts section */}
      {menuData.texMex.autres && (
        <Card className="bg-[#D4A85F] backdrop-blur-sm border-none shadow-lg">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="text-xl md:text-2xl font-bold text-black">{menuData.texMex.autres.title}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
              {menuData.texMex.autres.items.map((item, idx) => (
                <div key={idx} className="p-3 md:p-4 bg-white/50 rounded-lg hover:bg-white/70 transition-all duration-200 text-center">
                  <p className="font-bold text-black text-sm md:text-base">{item.quantity}</p>
                  <p className="text-base md:text-lg font-bold text-black mt-1">{item.price}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderAutres = () => (
    <div className="space-y-6 md:space-y-8">
      <Card className="bg-[#D4A85F] backdrop-blur-sm border-none shadow-lg">
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-xl md:text-2xl font-bold text-black">Frites</CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="grid grid-cols-3 gap-2 md:gap-4">
            {menuData.sides.frites.map((frite, idx) => (
              <div key={idx} className="p-3 md:p-4 bg-white/50 rounded-lg hover:bg-white/70 transition-all duration-200 text-center">
                <h4 className="font-bold text-black text-sm md:text-base">{frite.size}</h4>
                <p className="text-base md:text-lg font-bold text-black mt-1">{frite.price}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#D4A85F] backdrop-blur-sm border-none shadow-lg">
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-xl md:text-2xl font-bold text-black">Boissons</CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="grid grid-cols-3 gap-2 md:gap-4">
            {menuData.sides.drinks.map((drink, idx) => (
              <div key={idx} className="p-3 md:p-4 bg-white/50 rounded-lg hover:bg-white/70 transition-all duration-200 text-center">
                <h4 className="font-bold text-black text-sm md:text-base">{drink.size}</h4>
                <p className="text-base md:text-lg font-bold text-black mt-1">{drink.price}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#D4A85F] backdrop-blur-sm border-none shadow-lg">
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-xl md:text-2xl font-bold text-black">{menuData.desserts.title}</CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
            {menuData.desserts.items.map((dessert, idx) => (
              <div key={idx} className="flex justify-between items-center p-3 md:p-4 bg-white/50 rounded-lg hover:bg-white/70 transition-all duration-200">
                <h4 className="font-bold text-black text-sm md:text-base">{dessert.name}</h4>
                <span className="font-bold text-black text-sm md:text-base">{dessert.price}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-6xl h-[90vh] md:h-auto md:max-h-[90vh] overflow-hidden bg-[#1A1A1A] border-[#D4A85F] p-0 flex flex-col">
        {/* Header with category switcher */}
        <DialogHeader className="border-b border-[#D4A85F]/30 bg-black/50 p-4 md:p-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <DialogTitle className="text-2xl md:text-3xl font-bold text-[#D4A85F]">
              {categoryLabels[activeCategory]}
            </DialogTitle>
            <button
              onClick={onClose}
              className="text-[#D4A85F] hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full"
            >
              <X size={24} />
            </button>
          </div>

          {/* Category switcher - horizontal scroll on mobile */}
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            {Object.keys(categoryLabels).map((cat) => {
              const Icon = categoryIcons[cat];
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-all duration-200 text-sm md:text-base flex-shrink-0 ${
                    activeCategory === cat
                      ? 'bg-[#D4A85F] text-black'
                      : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  <Icon size={16} className="md:w-[18px] md:h-[18px]" />
                  <span>{categoryLabels[cat]}</span>
                </button>
              );
            })}
          </div>
        </DialogHeader>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};
